'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/store/authStore';
import { AppError } from '@/lib/errors';

/**
 * Global query error handler that catches and handles authentication errors
 * from React Query operations, preventing app crashes.
 */
export function QueryErrorHandler() {
  const queryClient = useQueryClient();
  const { clearAuth, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleAuthError = (error: unknown) => {
      // Check if it's an authentication error
      const isAuthError =
        (error instanceof AppError && error.statusCode === 401) ||
        (error instanceof Error &&
          (error.message.includes('401') ||
            error.message.includes('Unauthorized') ||
            error.message.includes('Session expired') ||
            error.message.includes('Authentication failed'))) ||
        (error as any)?.statusCode === 401;

      if (isAuthError && isAuthenticated) {
        // Clear authentication state
        clearAuth();

        // Redirect to login page with current path
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          const locale = currentPath.split('/')[1] || 'en';

          // Don't redirect if already on login page
          if (!currentPath.includes('/login')) {
            window.location.href = `/${locale}/login?redirect=${encodeURIComponent(currentPath)}`;
          }
        }
      }
    };

    // Create custom query cache with error handler
    const queryCache = queryClient.getQueryCache();
    const mutationCache = queryClient.getMutationCache();

    // Subscribe to query errors
    const queryUnsubscribe = queryCache.subscribe((event) => {
      if (event.type === 'updated' && event.query.state.status === 'error') {
        handleAuthError(event.query.state.error);
      }
    });

    // Subscribe to mutation errors
    const mutationUnsubscribe = mutationCache.subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'error') {
        handleAuthError(event.mutation.state.error);
      }
    });

    return () => {
      queryUnsubscribe();
      mutationUnsubscribe();
    };
  }, [queryClient, clearAuth, isAuthenticated]);

  // This component doesn't render anything
  return null;
}

export default QueryErrorHandler;
